import { Button } from "@mui/material";
import { FormEvent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { selectTrainingName } from "../../app/slices/TrainingSlice";
import PiDialog from "../../components/PiDialog";
import PiInputField from "../../components/PiInputField";
import { useSaveTraining } from "../../hooks/SaveTrainingHook";
import "./SaveTrainingAsDialog.css";

interface SaveTrainingAsDialogProps {
  open: boolean;
  onClose: () => void;
}

export default function SaveTrainingAsDialog({
  open,
  onClose,
}: SaveTrainingAsDialogProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const trainingName = useSelector(selectTrainingName);
  const { saveTrainingAs } = useSaveTraining();
  const [name, setName] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [hasError, setHasError] = useState(false);
  // Mirrors the latest committed name so handleSubmit never reads a stale
  // closure if the input commits (on blur) in the same tick as the click.
  const nameRef = useRef("");

  useEffect(() => {
    if (!open) return;

    const defaultName = trainingName
      ? t("save_as.default_name", { name: trainingName })
      : "";
    setName(defaultName);
    nameRef.current = defaultName;
    setHasError(false);
    setIsSaving(false);
  }, [open, t, trainingName]);

  const trimmedName = name.trim();
  const canSubmit = trimmedName.length > 0 && !isSaving;

  const handleNameChange = (value: string) => {
    setName(value);
    nameRef.current = value;
  };

  const handleClose = () => {
    if (isSaving) return;
    onClose();
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const submitName = nameRef.current.trim();
    if (submitName.length === 0 || isSaving) return;

    setIsSaving(true);
    setHasError(false);

    try {
      const result = await saveTrainingAs(submitName);
      if (result.success && result.trainingId) {
        onClose();
        navigate("/trainingEditor/" + result.trainingId);
        return;
      }

      setHasError(true);
    } finally {
      setIsSaving(false);
    }
  };

  const header = (
    <div className="savetrainingas-dialog-header">
      <div className="savetrainingas-dialog-title">{t("saveAs")}</div>
      <div className="savetrainingas-dialog-subtitle">
        {t("save_as.subtitle")}
      </div>
    </div>
  );

  const body = (
    <form
      className="savetrainingas-dialog-form"
      onSubmit={handleSubmit}
      data-testid="save-as-form"
    >
      <div className="savetrainingas-dialog-field">
        <PiInputField
          autoFocus
          label={t("trainingName")}
          placeholder={t("trainingName")}
          value={name}
          onChange={handleNameChange}
          disabled={isSaving}
          debounceTime={0}
        />
        {hasError && (
          <div role="alert" className="savetrainingas-dialog-error">
            {t("save_as.failed")}
          </div>
        )}
      </div>
      <div className="savetrainingas-dialog-actions">
        <div className="savetrainingas-dialog-action">
          <Button
            variant="piBlueOutlined"
            onClick={handleClose}
            disabled={isSaving}
          >
            {t("cancel")}
          </Button>
        </div>
        <div className="savetrainingas-dialog-action">
          <Button type="submit" variant="piBlue" disabled={!canSubmit}>
            {isSaving ? t("saving") : t("save")}
          </Button>
        </div>
      </div>
    </form>
  );

  return (
    <PiDialog
      open={open}
      onClose={handleClose}
      width="460px"
      height="300px"
      header={header}
      body={body}
    />
  );
}
